import React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import { Search, X } from 'lucide-react';

/**
 * 필드 타입 정의
 */
export type SearchFieldType = 'input' | 'select';

/**
 * Select 옵션 타입
 */
export interface SelectOption {
  value: string;
  label: string;
  className?: string;
}

/**
 * 필드 설정 인터페이스
 */
export interface SearchFieldConfig {
  /** 필드 키 (고유 식별자) */
  key: string;
  /** 필드 타입 */
  type: SearchFieldType;
  /** 라벨 텍스트 */
  label: string;
  /** placeholder (input 타입일 때) */
  placeholder?: string;
  /** Select 옵션들 (select 타입일 때) */
  options?: SelectOption[];
  /** Grid 컬럼 너비 (1-12, 기본값: 자동 계산) */
  colSpan?: number;
  /** 조건부 렌더링 함수 - false면 필드 숨김 */
  shouldShow?: (values: Record<string, any>) => boolean;
  /** 커스텀 onChange 핸들러 (기본 동작 대신 사용) */
  onChange?: (value: string, allValues: Record<string, any>) => void;
  /** Enter 키 핸들러 (input 타입일 때) */
  onEnter?: (value: string, allValues: Record<string, any>) => void;
  /** 추가 Input props */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** 추가 Select props */
  selectProps?: {
    className?: string;
    triggerClassName?: string;
  };
}

/**
 * SearchFilters 컴포넌트 Props
 */
export interface SearchFiltersProps {
  /** 필드 설정 배열 */
  fields: SearchFieldConfig[];
  /** 현재 필터 값들 */
  values: Record<string, any>;
  /** 값 변경 핸들러 */
  onChange: (key: string, value: any) => void;
  /** 검색 버튼 클릭 핸들러 */
  onSearch?: () => void;
  /** 초기화 버튼 클릭 핸들러 */
  onReset?: () => void;
  /** 로딩 상태 */
  loading?: boolean;
  /** 검색 버튼 표시 여부 (기본값: true) */
  showSearchButton?: boolean;
  /** 초기화 버튼 표시 여부 (기본값: true) */
  showResetButton?: boolean;
  /** 검색 버튼 텍스트 (기본값: "검색") */
  searchButtonText?: string;
  /** 초기화 버튼 텍스트 (기본값: "초기화") */
  resetButtonText?: string;
  /** 버튼 영역 컬럼 너비 (기본값: 자동 계산) */
  buttonColSpan?: number;
  /** 추가 클래스명 */
  className?: string;
}

/**
 * 유연한 검색 필터 컴포넌트
 * 
 * @example
 * ```tsx
 * <SearchFilters
 *   fields={[
 *     {
 *       key: 'status',
 *       type: 'select',
 *       label: '상태',
 *       options: [
 *         { value: 'all', label: '전체' },
 *         { value: 'active', label: '활성화' }
 *       ],
 *       colSpan: 2,
 *       shouldShow: (values) => values.showStatusFilter !== false
 *     },
 *     {
 *       key: 'search',
 *       type: 'input',
 *       label: '검색어',
 *       placeholder: '이메일 또는 이름으로 검색...',
 *       colSpan: 6,
 *       onEnter: (value, allValues) => handleSearch()
 *     }
 *   ]}
 *   values={filters}
 *   onChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
 *   onSearch={handleSearch}
 *   onReset={handleReset}
 * />
 * ```
 */
export const SearchFilters: React.FC<SearchFiltersProps> = ({
  fields,
  values,
  onChange,
  onSearch,
  onReset,
  loading = false,
  showSearchButton = true,
  showResetButton = true,
  searchButtonText = '검색',
  resetButtonText = '초기화',
  buttonColSpan,
  className = '',
}) => {
  // 표시할 필드만 필터링
  const visibleFields = fields.filter((field) => {
    if (field.shouldShow) {
      return field.shouldShow(values);
    }
    return true;
  });

  // 버튼 영역 너비 계산
  const calculateButtonColSpan = () => {
    if (buttonColSpan) return buttonColSpan;
    
    // 표시할 버튼 개수에 따라 자동 계산
    const buttonCount = (showSearchButton ? 1 : 0) + (showResetButton ? 1 : 0);
    if (buttonCount === 0) return 0;
    if (buttonCount === 1) return 2;
    return 2; // 기본값: 2칸 (버튼 2개)
  };

  // 검색어 필드의 colSpan 자동 계산 (나머지 공간 차지)
  const autoCalculateSearchColSpan = () => {
    const buttonSpan = calculateButtonColSpan();
    const selectFieldsSpan = visibleFields
      .filter(f => f.type === 'select')
      .reduce((sum, field) => sum + (field.colSpan || 2), 0);
    
    // 검색어 필드가 하나만 있고 colSpan이 지정되지 않았으면 자동 계산
    const searchFields = visibleFields.filter(f => f.type === 'input');
    if (searchFields.length === 1 && !searchFields[0].colSpan) {
      return 12 - selectFieldsSpan - buttonSpan;
    }
    return null;
  };

  const searchColSpan = autoCalculateSearchColSpan();

  // col-span 클래스명 매핑 (Tailwind 동적 클래스 문제 해결)
  // md와 lg 브레이크포인트 모두 지원 (lgSpan이 없으면 md와 동일한 값 사용)
  const getColSpanClass = (span: number, lgSpan?: number): string => {
    const spanMap: Record<number, string> = {
      1: 'md:col-span-1',
      2: 'md:col-span-2',
      3: 'md:col-span-3',
      4: 'md:col-span-4',
      5: 'md:col-span-5',
      6: 'md:col-span-6',
      7: 'md:col-span-7',
      8: 'md:col-span-8',
      9: 'md:col-span-9',
      10: 'md:col-span-10',
      11: 'md:col-span-11',
      12: 'md:col-span-12',
    };
    const lgSpanMap: Record<number, string> = {
      1: 'lg:col-span-1',
      2: 'lg:col-span-2',
      3: 'lg:col-span-3',
      4: 'lg:col-span-4',
      5: 'lg:col-span-5',
      6: 'lg:col-span-6',
      7: 'lg:col-span-7',
      8: 'lg:col-span-8',
      9: 'lg:col-span-9',
      10: 'lg:col-span-10',
      11: 'lg:col-span-11',
      12: 'lg:col-span-12',
    };
    const mdClass = spanMap[span] || 'md:col-span-2';
    // lgSpan이 지정되지 않으면 md와 동일한 값 사용
    const finalLgSpan = lgSpan !== undefined ? lgSpan : span;
    const lgClass = lgSpanMap[finalLgSpan] || '';
    return `${mdClass} ${lgClass}`.trim();
  };

  // 필드 렌더링
  const renderField = (field: SearchFieldConfig, _index: number) => {
    const fieldValue = values[field.key] ?? '';
    const colSpan = field.colSpan || (field.type === 'input' && searchColSpan ? searchColSpan : 2);

    // 조건부 렌더링 체크
    if (field.shouldShow && !field.shouldShow(values)) {
      return null;
    }

    if (field.type === 'input') {
      return (
        <div key={field.key} className={getColSpanClass(colSpan)}>
          <Label htmlFor={field.key}>{field.label}</Label>
          <Input
            id={field.key}
            type="text"
            placeholder={field.placeholder}
            value={fieldValue}
            onChange={(e) => {
              const newValue = e.target.value;
              if (field.onChange) {
                field.onChange(newValue, values);
              } else {
                onChange(field.key, newValue);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (field.onEnter) {
                  field.onEnter(fieldValue, values);
                } else if (onSearch) {
                  onSearch();
                }
              }
            }}
            className="w-full"
            disabled={loading}
            {...field.inputProps}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className={getColSpanClass(colSpan)}>
          <Label htmlFor={field.key}>{field.label}</Label>
          <Select
            value={fieldValue || 'all'}
            onValueChange={(value) => {
              if (field.onChange) {
                field.onChange(value, values);
              } else {
                onChange(field.key, value === 'all' ? '' : value);
              }
            }}
            disabled={loading}
          >
            <SelectTrigger
              id={field.key}
              className={`h-10 ${field.selectProps?.triggerClassName || ''}`}
            >
              <SelectValue placeholder={field.placeholder || '선택하세요'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem
                  key={`${field.key}-${option.value}`}
                  value={option.value}
                  className={option.className}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return null;
  };

  const buttonSpan = calculateButtonColSpan();

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        {visibleFields.map((field, index) => renderField(field, index))}
        
        {/* 버튼 영역 */}
        {(showSearchButton || showResetButton) && buttonSpan > 0 && (
          <div className={`${getColSpanClass(buttonSpan, buttonSpan)} flex items-end gap-2 flex-shrink-0`}>
            {showResetButton && (
              <Button
                variant="outline"
                onClick={onReset}
                disabled={loading}
                className="h-10"
              >
                <X className="mr-2 h-4 w-4" />
                {resetButtonText}
              </Button>
            )}
            {showSearchButton && (
              <Button
                onClick={onSearch}
                disabled={loading}
                className="h-10"
              >
                <Search className="mr-2 h-4 w-4" />
                {searchButtonText}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

